import { uploadToPresignedUrl } from '@/shared/utils/file-upload';

import {
	DEFAULT_VIDEO_HEIGHT,
	DEFAULT_VIDEO_WIDTH,
	MOBILE_RECORDING_FRAME_RATE,
	MOBILE_VIDEO_HEIGHT,
	MOBILE_VIDEO_WIDTH,
	RECORDING_FRAME_RATE,
} from './constants';

/**
 * Format seconds into MM:SS display format
 */
export function formatTime(seconds: number): string {
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export type UnmirroredStreamResult = {
	stream: MediaStream;
	stop: () => void;
};

type CreateUnmirroredStreamOptions = {
	stream: MediaStream;
	video: HTMLVideoElement;
	canvas: HTMLCanvasElement;
	isMobile?: boolean;
};

/**
 * Create an un-mirrored stream using canvas for recording
 * (Preview is mirrored for natural feel, but recording should be un-mirrored)
 *
 * Returns both the stream and a stop function for proper cleanup.
 * The animation loop is throttled to reduce CPU usage.
 * On mobile, uses lower frame rate (24fps) for better performance.
 */
export function createUnmirroredStream({
	stream,
	video,
	canvas,
	isMobile = false,
}: CreateUnmirroredStreamOptions): UnmirroredStreamResult | null {
	// Optimized canvas context options:
	// - alpha: false - No transparency needed, saves memory/processing
	// - desynchronized: true - Allows canvas updates independent of event loop
	const ctx = canvas.getContext('2d', {
		alpha: false,
		desynchronized: true,
	});
	if (!ctx) return null;

	let animationFrameId: number;
	let isRunning = true;

	// Use lower frame rate on mobile for CPU savings
	const targetFrameRate = isMobile ? MOBILE_RECORDING_FRAME_RATE : RECORDING_FRAME_RATE;

	// Set canvas size once - either immediately if dimensions available, or on loadedmetadata
	const setCanvasSize = () => {
		const width = video.videoWidth || (isMobile ? MOBILE_VIDEO_WIDTH : DEFAULT_VIDEO_WIDTH);
		const height = video.videoHeight || (isMobile ? MOBILE_VIDEO_HEIGHT : DEFAULT_VIDEO_HEIGHT);
		canvas.width = width;
		canvas.height = height;

		// Pre-set the horizontal flip transform once (optimization: avoid save/scale/restore per frame)
		// This transform flips the canvas horizontally: scale(-1, 1) + translate to correct position
		ctx.setTransform(-1, 0, 0, 1, width, 0);
	};

	if (video.videoWidth && video.videoHeight) {
		setCanvasSize();
	} else {
		video.addEventListener('loadedmetadata', setCanvasSize, { once: true });
	}

	// Throttle frame rate to target (~33.3ms for 30fps, ~41.7ms for 24fps)
	const frameInterval = 1000 / targetFrameRate;
	let lastFrameTime = 0;

	const drawFrame = (currentTime: number) => {
		if (!isRunning) return;

		animationFrameId = requestAnimationFrame(drawFrame);

		// Throttle to target frame rate - skip frame if not enough time has passed
		if (currentTime - lastFrameTime < frameInterval) return;
		lastFrameTime = currentTime;

		// Only draw if video has valid frame data
		// Transform is pre-set, so just drawImage (no save/scale/restore overhead)
		if (video.readyState >= video.HAVE_CURRENT_DATA) {
			ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
		}
	};

	// Start the animation loop
	animationFrameId = requestAnimationFrame(drawFrame);

	// Capture stream from canvas at target frame rate
	const canvasStream = canvas.captureStream(targetFrameRate);

	// Add audio tracks from original stream
	stream.getAudioTracks().forEach((track) => canvasStream.addTrack(track));

	// Cleanup function - stops animation loop and all tracks
	const stop = () => {
		isRunning = false;
		cancelAnimationFrame(animationFrameId);
		canvasStream.getTracks().forEach((track) => track.stop());
	};

	// Also cleanup when tracks end naturally
	canvasStream.getTracks().forEach((track) => {
		track.onended = () => {
			isRunning = false;
			cancelAnimationFrame(animationFrameId);
		};
	});

	return { stream: canvasStream, stop };
}

/**
 * Upload video to S3 using presigned URL
 */
export async function uploadVideoToS3(
	videoBlob: Blob,
	presignedUrl: string,
	onProgress?: (progress: number) => void
): Promise<string> {
	await uploadToPresignedUrl(videoBlob, presignedUrl, onProgress);

	// Extract the video path from the presigned URL
	const url = new URL(presignedUrl);
	const pathParts = url.pathname.split('/');
	const videoKey = pathParts.slice(2).join('/').split('?')[0];

	return videoKey;
}
